import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { GAMES } from "../data/games";
import { amount } from "../utils/plans";
import {
  isBuyable,
  planPrices,
  priceSummary,
  salesOpen,
  useLiveCatalog,
} from "../hooks/useCatalog";
import Particles from "../components/Particles";
import DecoBackdrop from "../components/DecoBackdrop";
import Zipline from "../components/Zipline";
import ClickBubbles from "../components/ClickBubbles";
import charHaze from "../assets/pictures/char-haze.webp";
/* Below the fold, with a few dozen menu captures and its own logic: loaded on
   its own so none of it delays the hero. Crawlers read the prerendered page,
   which does not use this component. */
const MenuExplorer = lazy(() => import("../components/home/MenuExplorer"));
import { GameTile, shortName } from "../components/home/GameTile";
import HomeFaq, { HOME_FAQ_PLAIN } from "../components/home/HomeFaq";
import { HeroArtBand, HeroFigure } from "../components/home/HeroHud";
import { MenuPanel, type Row } from "../components/home/MenuPanel";
import mountainsBg from "../assets/menu-teal/mountains-bg.webp";

import { useSeo } from "../components/Seo";
import NewHeroes from "../components/home/NewHeroes";
import VideoEmbed from "../components/VideoEmbed";
import { DEADLOCK_MONTAGE, videoJsonLd } from "../data/videos";
import ratkingIcon from "../assets/heroes/ratking-icon.png";
import babaIcon from "../assets/heroes/baba-icon.png";
import { ReviewsGrid, ReviewsSourceNote } from "../components/Reviews";
import { REVIEWS } from "../data/reviews";
interface OutletContext {
  openAuthModal: (mode: "login" | "signup") => void;
  openProductsDropdown: () => void;
  user: { uid: string } | null;
}

/* ---------- building blocks ---------- */

/* The Discord mark, as in the footer. */
const DiscordGlyph = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

/* ---- proof beside "Undetectable by design" ----
   Two panels rebuilt from the real Aim section (public/menu/menu-01), each
   paired with what it means for the person playing. The cards used to be an
   icon, a slogan and a pill reading "Aimbot"; these show the controls, and
   the second -- Headshot Cap -- is the most concrete answer on the site to
   "will this get me noticed?". */
const PROOF: { title: string; body: string; panel: { title: string; sub: string; rows: Row[] } }[] = [
  {
    title: "Aim that looks human.",
    body: "A small field of view and smoothing mean it only helps on targets you were already looking at. Nothing snaps across the screen.",
    panel: {
      title: "Aimbot",
      sub: "visible driver",
      rows: [
        { kind: "toggle", label: "Aimbot", on: true },
        { kind: "select", label: "Bone", value: "Neck" },
        /* Values as captured; max chosen so the thumb sits where it does
           in the capture (about a sixth of the way along). */
        { kind: "slider", label: "FOV", value: 8, max: 55, display: "8.0" },
        { kind: "slider", label: "Smoothing", value: 4, max: 25, display: "4.0" },
      ],
    },
  },
  {
    title: "Stats that stay believable.",
    body: "Headshot Cap holds your headshot rate under a ceiling you set. Once you are over it, the aim goes to the chest until you are back under.",
    panel: {
      title: "Headshot Cap",
      sub: "restraint",
      rows: [
        { kind: "toggle", label: "Headshot Cap", on: true },
        { kind: "slider", label: "Cap", value: 50, max: 100, display: "50%" },
        { kind: "select", label: "Once Over", value: "Aim Elsewhere" },
        { kind: "select", label: "Aim At", value: "Chest" },
      ],
    },
  },
];

const ProofCard = ({
  item,
  index,
  onHover,
}: {
  item: (typeof PROOF)[number];
  index: number;
  onHover: (hovered: boolean) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  /* The controls move once most of the card is on screen, not the moment its
     top edge appears -- otherwise the animation runs where nobody sees it. */
  const live = useInView(ref, { once: true, amount: 0.6 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
      }}
      className="card-spotlight edge-teal group relative grid gap-6 overflow-hidden rounded-2xl bg-ground-2 p-6 transition-all duration-300 hover:-translate-y-1 hover:z-50 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] sm:items-center"
    >
      <div className="relative order-2 sm:order-1">
        <h3 className="display mb-2 text-2xl text-white md:text-3xl">{item.title}</h3>
        <p className="text-sm leading-relaxed text-white/55">{item.body}</p>
      </div>
      <div className="relative order-1 sm:order-2">
        <MenuPanel {...item.panel} live={live} />
      </div>
    </motion.div>
  );
};

/* ---- "Take a closer look" pictures ---- */

const Tick = ({ className = "w-3 h-3" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

/* What actually happens after paying, as three steps that light in order.
   It replaces a mock order ("#4821 ... delivered in 0.4s") whose number and
   timing were invented. The steps are the real ones: access is granted when
   the payment settles, and the loader is on the Downloads page. */
const DeliveryTimeline = () => {
  const ref = useRef<HTMLDivElement>(null);
  const live = useInView(ref, { once: true, amount: 0.8 });
  const steps = ["Payment clears", "Access unlocked", "Loader on Downloads"];
  return (
    /* Three equal columns, each line drawn from its own circle's edge to the
       next circle's edge. The line used to be a flex item between two fixed
       columns wider than their circles, so it stopped 30-odd pixels short on
       both sides and never met either circle. */
    <div ref={ref} className="grid w-full max-w-lg grid-cols-3">
      {steps.map((step, i) => (
        <div key={step} className="relative flex flex-col items-center gap-2.5 px-1 text-center">
          {i < steps.length - 1 && (
            /* 22px is the circle's radius: from this circle's right edge to
               the next one's left edge, at the circles' centre line. */
            <span
              aria-hidden
              className="absolute left-[calc(50%+22px)] right-[calc(-50%+22px)] top-[21px] h-0.5 bg-white/10"
            >
              <span
                className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-500 motion-reduce:transition-none"
                style={{ width: live ? "100%" : "0%", transitionDelay: live ? `${i * 450 + 220}ms` : "0ms" }}
              />
            </span>
          )}
          <span
            className={`relative grid h-11 w-11 place-items-center rounded-full border transition-all duration-500 motion-reduce:transition-none ${
              live
                ? "border-primary bg-primary text-[#06211f] shadow-[0_0_24px_rgba(45,212,191,0.35)]"
                : "border-white/15 bg-[#0d2a2c] text-white/25"
            }`}
            style={{ transitionDelay: live ? `${i * 450}ms` : "0ms" }}
          >
            <Tick className="w-5 h-5" />
          </span>
          <span className="text-[0.76rem] font-medium leading-tight text-white/70">{step}</span>
        </div>
      ))}
    </div>
  );
};

/* The four titles as their own art, the betas marked. Straight from
   games.tsx, so a title added there shows up here. */
const TitleArt = () => (
  <div className="grid w-full max-w-[17rem] grid-cols-2 gap-2">
    {GAMES.filter(isBuyable).map((g) => (
      <div key={g.id} className="relative overflow-hidden rounded-lg border border-white/10 shadow-lg shadow-black/30">
        {g.dropdownImage && (
          <img
            src={g.dropdownImageSmall ?? g.dropdownImage}
            alt=""
            width={640}
            height={357}
            loading="lazy"
            decoding="async"
            className="block aspect-[16/8] w-full object-cover"
          />
        )}
        {g.badge === "Beta" && (
          <span className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-px text-[0.52rem] font-bold uppercase tracking-wider text-accent-pink">
            Beta
          </span>
        )}
      </div>
    ))}
  </div>
);

/* A question, someone typing, then the answer -- once, when it comes into
   view. The words are the same two as before; only the pause is new, and it
   is the part that reads as a person on the other end. */
const SupportChat = () => {
  const ref = useRef<HTMLDivElement>(null);
  const live = useInView(ref, { once: true, amount: 0.8 });
  const reduce = useReducedMotion();
  const [answered, setAnswered] = useState(false);
  useEffect(() => {
    if (!live) return;
    if (reduce) {
      setAnswered(true);
      return;
    }
    const t = setTimeout(() => setAnswered(true), 1300);
    return () => clearTimeout(t);
  }, [live, reduce]);
  return (
    <div ref={ref} className="w-56 space-y-2">
      <div className="max-w-[78%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.06] px-3.5 py-2 text-[0.76rem] text-white/70">
        Setup help?
      </div>
      <div className="flex justify-end">
        {answered ? (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="max-w-[85%] rounded-2xl rounded-br-md border border-primary/30 bg-primary/20 px-3.5 py-2 text-right text-[0.76rem] text-primary-light"
          >
            On it. Which game?
          </motion.div>
        ) : (
          <div
            aria-label="Typing"
            className="flex items-center gap-1 rounded-2xl rounded-br-md border border-primary/25 bg-primary/10 px-3.5 py-3"
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-light/80"
                style={{ animationDelay: `${i * 140}ms` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const VIGNETTES: Record<string, React.ReactNode> = {
  delivery: <DeliveryTimeline />,
  /* Was "External / Injectable". Shows the config manager instead. */
  configs: (
    <div className="w-56 rounded-xl border border-white/10 bg-[#081c1f]/90 p-3 space-y-1.5 shadow-xl shadow-black/30">
      {[{ n: "legit.cfg", on: true }, { n: "rage.cfg" }, { n: "hvh-backup.cfg" }].map((c) => (
        <div
          key={c.n}
          className={`flex items-center justify-between px-3 py-1.5 rounded-lg font-mono text-[0.74rem] ${c.on ? "bg-primary/15 text-primary-light" : "text-white/50"}`}
        >
          <span>{c.n}</span>
          {c.on && <span className="font-sans text-[0.64rem] font-semibold">loaded</span>}
        </div>
      ))}
    </div>
  ),
  /* The titles themselves, in their own artwork: four chips of text said
     "four titles" without showing one. */
  versions: <TitleArt />,
  /* The reply used to say "On it, 2 min" -- a response time nothing
     measures. */
  support: <SupportChat />,
  /* A plan picker and a receipt line: one charge, and that is the end of it.
     Checkout is a one-time payment and nothing is stored to renew. */
  plans: (
    <div className="w-56 rounded-xl border border-white/10 bg-[#081c1f]/90 p-3 shadow-xl shadow-black/30">
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-black/30 p-1 text-[0.74rem] font-semibold">
        <span className="rounded-md px-2 py-1.5 text-center text-white/55">Weekly</span>
        <span className="rounded-md bg-primary px-2 py-1.5 text-center text-[#06211f]">Monthly</span>
      </div>
      <div className="mt-3 flex items-center justify-between px-1 text-[0.72rem]">
        <span className="text-white/60">Charged once</span>
        <span className="inline-flex items-center gap-1 font-semibold text-primary-light">
          <Tick />
          No renewal
        </span>
      </div>
    </div>
  ),
};

type CloserLink = { to: string; label: string; external?: boolean };

/* Each card links where its subject is explained, or nowhere -- all six used
   to end in the same "Learn more" to /products. "Trusted provider" said
   nothing a reader could check, and "Tuned to stay quiet" is now covered by
   the aim panels beside "Undetectable by design". */
const CLOSER_FEATURES: { title: string; body: string; viz: string; wide?: boolean; link?: CloserLink }[] = [
  {
    title: "Instant delivery",
    body: "Access unlocks the moment your payment clears, and the loader is waiting on your Downloads page. No queue, no manual step, no waiting on support.",
    viz: "delivery",
    wide: true,
  },
  {
    title: "No subscription",
    body: "Every plan is a single payment for a set period. Nothing renews on its own, so there is nothing to cancel.",
    viz: "plans",
    link: { to: "/products", label: "See the plans" },
  },
  { title: "Configs you can keep", body: "Save a setup per lobby, load it back in a click, and move it between machines.", viz: "configs" },
  {
    title: "Four titles, one account",
    /* Not "open beta" for Roblox: its purchases are switched off while it
       is tested, so "open" promised something the product page refuses. */
    body: "Deadlock and FiveM are live now, with CS2 and Roblox in beta.",
    viz: "versions",
    link: { to: "/products", label: "See all titles" },
  },
  {
    title: "Active support",
    body: "Whatever the issue may be, a real team is one message away, around the clock.",
    viz: "support",
    link: { to: "https://discord.gg/d64RvvK9Ta", label: "Open Discord", external: true },
  },
];

export default function Home() {
  const { feed } = useLiveCatalog();

  /* The cheapest plan anyone can actually buy, across the titles actually on
     sale -- from the live feed, so a price change or a title switched off in
     admin moves it. The entry price is the shortest plan's own price, never a
     per-week rate derived from a longer plan that is not for sale on its own. */
  const cheapestEntry = (() => {
    let best: { amount: number; display: string; unit: string } | null = null;
    for (const g of GAMES) {
      if (!salesOpen(g, feed)) continue;
      const entry = priceSummary(planPrices(g, feed))?.entry;
      if (!entry) continue;
      const value = amount(entry.display);
      if (!isFinite(value)) continue;
      if (!best || value < best.amount) best = { amount: value, display: entry.display, unit: entry.unit };
    }
    return best;
  })();

  useSeo({
    title: "Veyra — Deadlock Cheats & Hacks, Aimbot & ESP | CS2, FiveM",
    description:
      "Deadlock cheats and hacks with aimbot, ESP wallhack, glow and hero scripts. Also CS2, FiveM and Roblox. Instant delivery, 24/7 support.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          mainEntity: HOME_FAQ_PLAIN.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        videoJsonLd(DEADLOCK_MONTAGE, "https://veyra.services/"),
      ],
    },
    canonicalPath: "/",
  });

  const { user, openAuthModal } = useOutletContext<OutletContext>();
  const heroRef = useRef<HTMLElement>(null!);
  const ctaRef = useRef<HTMLDivElement>(null!);
  const [cardHovered, setCardHovered] = useState(false);
  const [ctaHovered, setCtaHovered] = useState(false);
  const [ctaOverflowVisible, setCtaOverflowVisible] = useState(false);
  /* React 19 types require an initial value: useRef<T>() with no argument
     is a compile error, which broke the build on arrival. */
  const ctaTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (ctaHovered) {
      clearTimeout(ctaTimerRef.current);
      setCtaOverflowVisible(true);
    } else {
      ctaTimerRef.current = setTimeout(() => setCtaOverflowVisible(false), 500);
    }
    return () => clearTimeout(ctaTimerRef.current);
  }, [ctaHovered]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Hero bloom behind the artwork, killed almost immediately on scroll — the
     glow belongs to the landing moment, not to the page.
     Driven from scrollY, which is a raw pixel offset, rather than from a
     scroll *progress* value: progress depends on framer measuring the
     scrollable range, and that measurement has already proved unreliable on
     this page. Absolute pixels cannot be measured wrong. */
  const { scrollY } = useScroll();
  const heroGlow = useTransform(scrollY, [0, 90, 240], [1, 0.55, 0]);




  return (
    <div className="relative overflow-hidden bg-ground">
      {/* Hovering a card softens the rest of the page. Kept light -- 0.6px
          and 4% darker, down from 1.5px and 8%, which made the page look out
          of focus every time the pointer crossed a card. */}
      <div
        className="fixed inset-0 z-40 pointer-events-none"
        style={{
          backdropFilter: cardHovered ? "blur(0.6px)" : "blur(0px)",
          WebkitBackdropFilter: cardHovered ? "blur(0.6px)" : "blur(0px)",
          background: cardHovered ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0)",
          opacity: cardHovered ? 1 : 0,
          transition: "backdrop-filter 0.4s ease, -webkit-backdrop-filter 0.4s ease, background 0.4s ease, opacity 0.4s ease",
        }}
      />
      {/* The Cursed Apple, behind the whole upper page. Extends well past the
          hero and fades out, so there is no seam where the city stops. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[100vh] [mask-image:linear-gradient(to_bottom,black_0%,black_58%,transparent_100%)]"
      >
        <DecoBackdrop variant="skyline" className="opacity-95" />
      </div>

      {/* Zipline network running the full height of the page, with someone on
          it. The cable draws itself just ahead of the rider as you scroll. */}
      <Zipline />
      <ClickBubbles />
      {/* ===================== HERO ===================== */}
      <section
        ref={heroRef}
        className="relative min-h-[calc(100vh_-_4rem)] flex items-center px-6 pt-10 pb-10 overflow-hidden"
      >
        {/* Only the arch is scoped to the hero; the city sits at page level
            below so it can run past this section instead of being cut by it. */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <DecoBackdrop variant="arch" className="opacity-70" />
        </div>

        <Particles containerRef={heroRef} zIndex={1} minLifetime={15} maxLifetime={25} particleCount={60} />

        <div className="relative z-10 max-w-6xl mx-auto w-full grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-center lg:text-left">
            {/* The page's H1 is this line, not the slogan below it. Search
                engines weight the H1 heavily, and "The unfair advantage"
                names nothing anyone searches for; what this site sells, for
                which games, is exactly what they do search. The slogan keeps
                its look as a paragraph. (Was "Trusted by 500+" once: a count
                with nothing on the page to back it up.) */}
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="edge-teal inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 mb-7 rounded-full bg-ground-2/70 text-white/75 text-[0.64rem] sm:text-[0.72rem] font-semibold tracking-[0.12em] sm:tracking-[0.2em] uppercase backdrop-blur"
            >
              <span aria-hidden className="w-1.5 h-1.5 shrink-0 rounded-full bg-primary-light animate-pulse" />
              <span>
                Cheats for{" "}
                {GAMES.filter((g) => salesOpen(g, feed))
                  .map((g) => shortName(g))
                  .join(" · ")}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="display text-white text-6xl sm:text-7xl md:text-8xl mb-6"
            >
              The unfair
              <br />
              <span className="text-primary-light">advantage.</span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="text-lg md:text-xl text-white/60 max-w-md mx-auto lg:mx-0 leading-relaxed"
            >
              {/* Was "Premium enhancement software, engineered to keep you a
                  step ahead and completely undetected" -- it named nothing you
                  get. This names the three things, and the one fact about
                  buying that people ask first. */}
              Aimbot, ESP and scripts, built to stay out of sight. Pay once,
              and access unlocks the moment your payment clears.
            </motion.p>

            {/* What is new, the way a launch page says it: one line, the two
                faces, and a jump to the section that explains it. */}
            <motion.a
              href="#new-heroes"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="group mt-6 inline-flex items-center gap-3 rounded-full border border-white/10 bg-ground-2/70 py-1.5 pl-1.5 pr-4 text-sm text-white/75 backdrop-blur transition-colors hover:border-primary/40 hover:text-white"
            >
              <span className="flex -space-x-2" aria-hidden>
                <img src={ratkingIcon} width={28} height={28} alt="" className="h-7 w-7 rounded-full bg-ground ring-2 ring-ground-2" />
                <img src={babaIcon} width={28} height={28} alt="" className="h-7 w-7 rounded-full bg-ground ring-2 ring-ground-2" />
              </span>
              <span>
                <span className="font-semibold text-accent-pink">New</span>
                <span className="mx-1.5 text-white/25">·</span>
                Violet, Deadman Danny, Solomon and Rat King scripts for Deadlock
              </span>
              <span className="text-primary-light transition-transform group-hover:translate-x-0.5" aria-hidden>
                →
              </span>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mt-9"
            >
              {/* A real link, not a button that calls navigate(). The
                  homepage's main call to action was invisible to a crawler --
                  it passed nothing to /products and could not be followed --
                  and a reader could not middle-click it or open it in a new
                  tab either. */}
              <Link
                to="/products"
                className="group inline-block px-8 py-3.5 rounded-full text-[#06211f] font-semibold text-base bg-primary hover:bg-primary-dark shadow-lg shadow-primary/25 transition-all duration-200 hover:-translate-y-0.5"
              >
                Explore products
                <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
              </Link>
              <a
                href="https://discord.gg/d64RvvK9Ta"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3.5 rounded-full font-semibold text-base bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white transition-all duration-200 flex items-center justify-center gap-2.5"
              >
                {/* Was "Community", which did not say where it went. */}
                <DiscordGlyph />
                Join the Discord
              </a>
            </motion.div>

            {/* Plain links to the guides, in the words people search: the
                homepage is the site's strongest page in Search Console and
                linked to them only from inside the FAQ. */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-5 text-sm text-white/50"
            >
              <Link to="/deadlock-cheats" className="text-white/75 underline-offset-4 hover:text-primary-light hover:underline">
                Deadlock cheats guide
              </Link>
              <span className="mx-2 text-white/20">·</span>
              <Link to="/free-deadlock-cheat" className="text-white/75 underline-offset-4 hover:text-primary-light hover:underline">
                Free Deadlock cheat
              </Link>
              <span className="mx-2 text-white/20">·</span>
              <Link to="/deadlock-hero-scripts" className="text-white/75 underline-offset-4 hover:text-primary-light hover:underline">
                Deadlock hero scripts
              </Link>
              {/* The aimbot and ESP guides rank for "deadlock aimbot" and
                  "deadlock esp" (34th in Search Console) but were only linked
                  from other guides; a link from the homepage carries the most
                  weight the site has. */}
              <span className="mx-2 text-white/20">·</span>
              <Link to="/deadlock-aimbot" className="text-white/75 underline-offset-4 hover:text-primary-light hover:underline">
                Deadlock aimbot
              </Link>
              <span className="mx-2 text-white/20">·</span>
              <Link to="/deadlock-esp-wallhack" className="text-white/75 underline-offset-4 hover:text-primary-light hover:underline">
                Deadlock ESP
              </Link>
            </motion.p>

            {/* The price, read from the live feed across the titles actually
                on sale, and the two terms people check before paying. */}
            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="mt-7 flex flex-wrap justify-center gap-2 lg:justify-start"
            >
              {/* Badges rather than a line of small grey text: these are the
                  three facts people check before paying, and at text-sm/60
                  they were the easiest thing on the screen to miss. */}
              {cheapestEntry && (
                <li className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-[0.82rem] text-white/75 backdrop-blur">
                  From <span className="font-semibold text-white">{cheapestEntry.display}</span> a {cheapestEntry.unit}
                </li>
              )}
              {[
                ["Instant access", "M13 3L4 14h6l-1 7 9-11h-6l1-7z"],
                ["No subscription", "M5 13l4 4L19 7"],
              ].map(([fact, icon]) => (
                <li
                  key={fact}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[0.82rem] text-white/70 backdrop-blur"
                >
                  <svg className="h-3.5 w-3.5 text-primary-light" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d={icon} />
                  </svg>
                  {fact}
                </li>
              ))}
            </motion.ul>

            <HeroArtBand />
          </div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="relative hidden lg:flex justify-center items-center"
          >
            {/* Bloom behind her, dying on scroll. */}
            <motion.div
              aria-hidden
              style={{ opacity: heroGlow }}
              className="pointer-events-none absolute w-[34rem] h-[34rem] rounded-full blur-[70px] bg-[radial-gradient(circle,rgba(45,212,191,0.34)_0%,rgba(45,212,191,0.12)_44%,transparent_70%)]"
            />
            <HeroFigure />
            {/* A pool of light at her feet: she was floating on the skyline.
                Offset by the figure's own translate so it stays under her. */}
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-[-2.6rem] left-1/2 h-16 w-[22rem] translate-x-[calc(-50%+70px)] rounded-[50%] bg-[radial-gradient(closest-side,rgba(45,212,191,0.32),rgba(45,212,191,0.08)_55%,transparent)]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-[-1.6rem] left-1/2 h-5 w-[12rem] translate-x-[calc(-50%+70px)] rounded-[50%] bg-black/70 blur-md"
            />
          </motion.div>
        </div>
      </section>

      {/* ===================== THE LINEUP ===================== */}
      <section className="relative z-10 px-6 pb-8 pt-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 flex items-end justify-between gap-4">
            <h2 className="display text-4xl text-white md:text-5xl">Pick your game</h2>
            <Link
              to="/products"
              className="shrink-0 text-sm font-semibold text-primary-light transition-colors hover:text-white"
            >
              All products →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {GAMES.filter(isBuyable).map((g, i) => (
              <GameTile key={g.id} game={g} feed={feed} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ===================== THE VIDEO ===================== */}
      {/* The latest video, embedded on the page with the most weight: it is
          what a "deadlock cheats" search shows from YouTube, and the
          VideoObject markup lets the homepage itself appear as a video
          result. */}
      <section className="relative z-10 px-6 py-10 lg:py-14">
        <div className="mx-auto max-w-4xl">
          <h2 className="display text-4xl text-white md:text-5xl">Watch it in a real match</h2>
          <p className="mt-3 max-w-2xl text-white/60">
            Aimbot, ESP and hero scripts in real Deadlock matches, with the new heroes. 1:33, no menus, just the game.
          </p>
          <VideoEmbed video={DEADLOCK_MONTAGE} id="video" />
        </div>
      </section>

      {/* ===================== NEW HEROES ===================== */}
      <NewHeroes />

      {/* ===================== UNDETECTABLE ===================== */}
      {/* One section, statement beside proof. It used to be a centred heading,
          then a full-width card that was mostly empty space and a faint V,
          then the two feature cards as a separate section -- a screen and a
          half to say one thing. Its button read "Explore all features" and
          went to /products; it now goes to the feature breakdown. */}
      <section className="relative py-14 px-6 overflow-hidden lg:py-20" style={{ zIndex: cardHovered ? 45 : undefined }}>
        <DecoBackdrop
          variant="colonnade"
          className="opacity-60 [mask-image:linear-gradient(to_bottom,transparent,black_40%,black_70%,transparent)]"
        />
        <div className="relative max-w-6xl mx-auto grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:items-center lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.65 }}
          >
            <h2 className="display text-5xl text-white md:text-6xl">
              Undetectable
              <br />
              <span className="text-primary-light">by design.</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
              Built from the ground up to stay invisible. Combat modules engineered to last, with no
              temporary bypasses and no drama. Just an edge that keeps working.
            </p>
            {/* What "undetectable" rests on, in terms a buyer can check: each
                of these is how the product behaves, and the guides say the
                same. The column was a paragraph and a button beside two tall
                cards. */}
            {/* On a solid panel, like the cards beside it: the zipline runs
                diagonally through this column, and over bare text its cable
                and rider crossed the words. */}
            <ul className="relative z-10 mt-7 max-w-md space-y-3 rounded-2xl border border-white/8 bg-ground-2 p-5 shadow-xl shadow-black/30">
              {[
                ["Pulled the moment it is unsafe", "A build is taken offline when a game update or a detection makes it risky."],
                ["No stale build can run", "The loader refuses a withdrawn build, so nobody plays on one by accident."],
                ["Out of recordings", "Stream proof keeps the menu and overlays off captures and streams."],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/15 text-primary-light">
                    <Tick className="h-3.5 w-3.5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white">{title}</span>
                    <span className="block text-sm leading-relaxed text-white/50">{body}</span>
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to="/features"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-[#06211f] shadow-lg shadow-primary/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-light"
            >
              Explore all features
              <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
          </motion.div>

          <div className="grid gap-5">
            {PROOF.map((item, i) => (
              <ProofCard key={item.title} item={item} index={i} onHover={setCardHovered} />
            ))}
            <p className="px-1 text-xs text-white/40">
              Panels rebuilt from the Deadlock menu. Every title has its own.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== INSIDE THE MENU ===================== */}
      <section className="relative py-14 px-6 lg:py-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[12%] right-[6%] w-[440px] h-[440px] rounded-full bg-primary/8 blur-[150px]" />
        </div>
        <div className="max-w-6xl mx-auto relative">
          {/* left-aligned: a centred eyebrow + heading + subtitle stack is the
              most templated section header there is */}
          <div className="max-w-xl mb-12">
            <h2 className="display text-5xl md:text-6xl text-white">
              Inside the menu
            </h2>
            <p className="text-white/55 mt-3">
              Real captures, section by section. Pick a game and look around.
            </p>
          </div>
          <Suspense fallback={<div className="min-h-[34rem] rounded-2xl border border-white/[0.06] bg-white/[0.02]" />}>
            <MenuExplorer />
          </Suspense>
        </div>
      </section>

      {/* ===================== TAKE A CLOSER LOOK ===================== */}
      {/* The two decorative curves that used to sit here ran straight across
          the cards, which read as a rendering fault rather than decoration. */}
      <section className="relative py-14 px-6 overflow-hidden lg:py-20" style={{ zIndex: cardHovered ? 45 : undefined }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-[10%] left-[6%] w-[420px] h-[420px] rounded-full bg-accent-pink/5 blur-[150px]" />
        </div>

        <div className="max-w-6xl mx-auto relative">
          {/* Was "Take a closer look / A few more things worth knowing",
              which described nothing. Every card here is about buying and
              owning it. */}
          <div className="max-w-xl mb-10">
            <h2 className="display text-5xl md:text-6xl text-white">
              After you buy
            </h2>
            <p className="text-white/55 mt-3">What delivery, plans, configs and support actually look like.</p>
          </div>

          {/* Not six equal boxes: delivery -- the thing a buyer most wants
              settled -- gets the wide card, and its picture is the actual
              sequence rather than a mock order number. */}
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {CLOSER_FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                onMouseEnter={() => setCardHovered(true)}
                onMouseLeave={() => setCardHovered(false)}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
                /* Card surfaces have to occlude: at bg-white/[0.02] the zipline
                   rider showed through and looked like it sat on the card. */
                className={`card-spotlight edge-teal group relative flex h-full flex-col rounded-2xl bg-ground-2 hover:bg-ground-3 hover:-translate-y-1 hover:z-50 transition-all duration-300 overflow-hidden ${
                  f.wide ? "md:col-span-2" : ""
                }`}
              >
                {/* A faint dot grid behind each picture, so it sits on a
                    surface instead of floating in an empty box. */}
                <div
                  className={`relative flex items-center justify-center border-b border-white/5 bg-linear-to-b from-white/[0.03] to-transparent px-6 ${
                    f.wide ? "h-48" : "h-44"
                  }`}
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-60 [background-image:radial-gradient(rgba(255,255,255,0.09)_1px,transparent_1px)] [background-size:14px_14px] [mask-image:radial-gradient(ellipse_70%_75%_at_50%_50%,black,transparent)]"
                  />
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-40 h-20 bg-primary/10 blur-[50px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  {VIGNETTES[f.viz]}
                </div>
                <div className={`flex flex-col flex-1 p-6 ${f.wide ? "md:flex-row md:items-end md:justify-between md:gap-8" : ""}`}>
                  <div>
                    {/* One size up: at text-xl the condensed face ran its
                        letters together. */}
                    <h3 className={`display text-white mb-2 tracking-[0.015em] ${f.wide ? "text-3xl" : "text-2xl"}`}>{f.title}</h3>
                    <p className={`text-white/55 text-sm leading-relaxed ${f.wide ? "max-w-lg" : "mb-5"}`}>{f.body}</p>
                  </div>
                  {f.link &&
                    (f.link.external ? (
                      <a
                        href={f.link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto self-start inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary-light hover:text-white transition-colors"
                      >
                        {f.link.label}
                        <span className="transition-transform group-hover:translate-x-0.5">›</span>
                      </a>
                    ) : (
                      <Link
                        to={f.link.to}
                        className="mt-auto self-start inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary-light hover:text-white transition-colors"
                      >
                        {f.link.label}
                        <span className="transition-transform group-hover:translate-x-0.5">›</span>
                      </Link>
                    ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== REVIEWS ===================== */}
      {/* Customers' own words from the Discord #reviews channel, with names
          and dates. No stars: nobody gave a rating. */}
      <section className="relative px-6 py-14 lg:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <h2 className="display text-5xl text-white md:text-6xl">
              What players <span className="text-primary-light">say.</span>
            </h2>
            <ReviewsSourceNote />
          </div>
          <ReviewsGrid reviews={REVIEWS} />
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="relative px-6 py-14 lg:py-20">
        <div className="max-w-6xl mx-auto">
          <HomeFaq />
        </div>
      </section>

      {/* ===================== CTA TEAL PANEL ===================== */}
      <section className="relative pt-10 pb-36 px-6 overflow-hidden lg:pt-14" style={{ zIndex: ctaHovered ? 45 : undefined }}>
        <img
          src={mountainsBg}
          alt=""
          aria-hidden
          className="absolute inset-x-0 bottom-0 w-full h-[420px] object-cover object-bottom pointer-events-none select-none [mask-image:linear-gradient(to_bottom,transparent,black_45%)]"
          loading="lazy"
          decoding="async"
        />
        <div ref={ctaRef} className="absolute inset-0 pointer-events-none">
          <Particles containerRef={ctaRef} zIndex={0} />
        </div>
        <div className={`max-w-6xl mx-auto relative ${ctaHovered ? "z-50" : "z-10"}`}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8 }}
            onMouseEnter={() => setCtaHovered(true)}
            onMouseLeave={() => setCtaHovered(false)}
            className="group relative max-w-5xl mx-auto"
          >
            <div
              className={`relative rounded-3xl border border-primary/25 px-8 py-12 md:py-16 ${ctaOverflowVisible ? "" : "overflow-hidden"}`}
              style={{
                background:
                  "radial-gradient(105% 130% at 0% 0%, rgba(45,212,191,0.30) 0%, transparent 58%), radial-gradient(70% 90% at 100% 110%, rgba(219,73,166,0.20) 0%, transparent 62%), linear-gradient(128deg, #0e3b3a 0%, #0a2427 52%, #071518 100%)",
                borderRadius: "1.5rem",
              }}
            >
              <div aria-hidden className="absolute inset-0 opacity-[0.06] pointer-events-none rounded-3xl overflow-hidden" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,.6) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
              <div aria-hidden className="hidden md:block absolute right-24 top-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-primary/25 blur-[90px] pointer-events-none" />
              <img
                src={charHaze}
                alt=""
                aria-hidden
                /* The last thing on the page; it was loading with the hero. */
                loading="lazy"
                decoding="async"
                className={`hidden md:block absolute right-6 bottom-0 h-[112%] w-auto object-contain object-bottom pointer-events-none select-none [mask-image:linear-gradient(to_bottom,black_86%,transparent)] origin-bottom transition-all duration-500 ease-out ${ctaHovered ? "scale-[1.05] z-10" : "scale-[0.9] z-0"}`}
              />
              {/* Was "Why wait? Claim your edge. Join the players already
                  staying one step ahead" -- urgency and a crowd, with nothing
                  a reader could weigh. By the bottom of the page the question
                  left is what it costs and what happens after paying. */}
              <div className="relative text-center md:text-left md:max-w-lg">
                <h2 className="display text-4xl md:text-5xl text-white mb-4">
                  One payment.
                  <br />
                  <span className="text-primary-light">Then you're in.</span>
                </h2>
                <p className="text-white/65 text-base md:text-lg mb-8 max-w-md mx-auto md:mx-0 leading-relaxed">
                  {cheapestEntry ? `From ${cheapestEntry.display} a ${cheapestEntry.unit}, unlocked` : "Unlocked"} the
                  moment your payment clears. No subscription, nothing to cancel, and a real team on
                  Discord if you get stuck.
                </p>
                <div className="flex flex-col items-center gap-3 sm:flex-row md:items-start">
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 px-9 py-3.5 rounded-full bg-primary text-[#06211f] font-semibold text-base shadow-lg shadow-primary/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-light"
                  >
                    Pick your game
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </Link>
                  {/* Signed in, the useful next step is the loader, not a
                      sign-up form. */}
                  {user ? (
                    <Link
                      to="/downloads"
                      className="inline-flex items-center px-7 py-3.5 rounded-full border border-white/15 bg-white/5 font-semibold text-white transition-colors hover:bg-white/10"
                    >
                      Go to Downloads
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openAuthModal("signup")}
                      className="inline-flex items-center px-7 py-3.5 rounded-full border border-white/15 bg-white/5 font-semibold text-white transition-colors hover:bg-white/10"
                    >
                      Create an account
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
